import { useState, useMemo } from "react"
import { useChat } from "@ai-sdk/react"
import { DefaultChatTransport } from "ai"
import { useAppDispatch, useAppSelector } from "~/ui-rtk/app/hooks"
import { chatbotActions } from "~/ui-rtk/app/slices/chatbot.slice"
import { ChatInput } from "./ChatInput"
import { DataFreshnessWarning } from "./DataFreshnessWarning"
import { MessageList } from "./MessageList"
import { PersonaSelector } from "./PersonaSelector"
import { QuickPrompts } from "./QuickPrompts"

export function ChatPanel() {
  const dispatch = useAppDispatch()
  const isPanelOpen = useAppSelector(state => state.chatbotSlice.isPanelOpen)
  const selectedPersona = useAppSelector(
    state => state.chatbotSlice.selectedPersona,
  )

  const [input, setInput] = useState("")

  const transport = useMemo(
    () =>
      new DefaultChatTransport({
        api: `${import.meta.env.VITE_API_BASE_URL || ""}/chatbot/message`,
        credentials: "include",
        body: { persona: selectedPersona },
      }),
    [selectedPersona],
  )

  const { messages, sendMessage, status, setMessages, error, clearError } =
    useChat({
      transport,
    })

  const isLoading = status === "submitted" || status === "streaming"

  const handleSend = (text: string) => {
    if (!text.trim() || isLoading) return
    setInput("")
    clearError()
    void sendMessage({ text })
  }

  const handleQuickPrompt = (prompt: string) => {
    void sendMessage({ text: prompt })
  }

  const handleReset = () => {
    setMessages([])
    setInput("")
    clearError()
  }

  if (!isPanelOpen) return null

  return (
    <div className="fixed bottom-20 right-4 w-[500px] h-[680px] bg-system-blue-800 rounded-2xl shadow-[0_0_40px_rgba(165,124,251,0.12)] border border-system-blue-600 flex flex-col z-50 overflow-hidden">
      {/* Animated gradient accent line */}
      <div className="h-0.5 shrink-0 chatbot-header-gradient" />

      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-system-blue-600">
        <PersonaSelector disabled={isLoading} />
        <div className="flex items-center gap-1">
          {(messages.length > 0 || error) && (
            <button
              type="button"
              className="btn btn-ghost btn-xs btn-circle tooltip tooltip-bottom"
              data-tip="New chat"
              onClick={handleReset}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-3.5 w-3.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182"
                />
              </svg>
            </button>
          )}
          <button
            type="button"
            className="btn btn-ghost btn-xs btn-circle"
            onClick={() => dispatch(chatbotActions.setPanel(false))}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
      </div>

      <DataFreshnessWarning />

      {/* Messages or Quick Prompts */}
      {messages.length === 0 && !error ? (
        <div className="flex-1 overflow-y-auto">
          <QuickPrompts onSelect={handleQuickPrompt} />
        </div>
      ) : (
        <MessageList
          messages={messages}
          isLoading={isLoading}
          status={status}
          error={error}
        />
      )}

      {/* Input */}
      <ChatInput
        input={input}
        onInputChange={setInput}
        onSubmit={handleSend}
        isLoading={isLoading}
      />
    </div>
  )
}
